import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Calling from '../../api/Calling';
import generateCaptcha from '../../components/captcha';
import VideoNavbar from './VideoNavbar';
import Loader from '../../components/Loader';

const VideoShow = () => {
    const [videos, setVideos] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loader, setLoader] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [category, setCategory] = useState(null);
    const [webFilter, setWebFilter] = useState('all'); // 'all', 'true', 'false'
    const [error, setError] = useState(null);
    const [selectedVideos, setSelectedVideos] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [webStatusLoading, setWebStatusLoading] = useState(false);

    // Define the limit for items per page
    const limit = 100;

    useEffect(() => {
        fetchVideos();
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchVideos();
    }, [currentPage, category, webFilter]);

    // Effect to handle "select all" checkbox
    useEffect(() => {
        if (selectAll) {
            setSelectedVideos(videos.map(video => video._id));
        } else {
            setSelectedVideos([]);
        }
    }, [selectAll, videos]);

    const fetchCategories = async () => {
        try {
            const response = await Calling.getAllCategories();
            if (response.status && response.data) {
                setCategories(response.data);
            } else {
                console.error('Failed to fetch categories:', response.message);
                toast.error('Failed to load categories');
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Error loading categories');
        }
    };

    const fetchVideos = async () => {
        try {
            setLoader(true);
            setError(null);

            // Update API call to include webFilter parameter
            const response = await Calling.getVideo(currentPage, limit, category, webFilter);

            if (response.status) {
                setVideos(response.data);
                setTotalPages(response.totalPage);
                setTotalRecords(response.totalRecord);
                // Reset selection when videos change
                setSelectedVideos([]);
                setSelectAll(false);
            } else {
                setError(response.message || 'Failed to fetch videos');
                console.error('Failed to fetch videos:', response.message);
                toast.error(response.message || 'Failed to load videos');
            }
        } catch (error) {
            setError('Error connecting to server');
            console.error('Error fetching videos:', error);
            toast.error('Connection error - Please try again later');
        } finally {
            setLoader(false);
        }
    };

    const handleCategoryChange = (newCategory) => {
        setCategory(newCategory);
        setCurrentPage(1);
    };

    const handleWebFilterChange = (filter) => {
        setWebFilter(filter);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle video selection
    const handleVideoSelect = (videoId) => {
        setSelectedVideos(prev => {
            if (prev.includes(videoId)) {
                // If already selected, remove it
                const newSelection = prev.filter(id => id !== videoId);
                // Update selectAll state
                if (newSelection.length === 0) {
                    setSelectAll(false);
                }
                return newSelection;
            } else {
                // If not selected, add it
                const newSelection = [...prev, videoId];
                // Update selectAll state if all videos are selected
                if (newSelection.length === videos.length) {
                    setSelectAll(true);
                }
                return newSelection;
            }
        });
    };

    // Handle single video delete
    const handleSingleDelete = async (videoId) => {
        const content = `Are you sure you want to delete this video? Solve the math problem to confirm.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                setDeleteLoading(true);
                const response = await Calling.deleteVideo({ id: [videoId] });

                if (response.status) {
                    toast.success('Video deleted successfully');
                    fetchVideos(); // Refresh the list
                } else {
                    toast.error(response.message || 'Failed to delete video');
                }
            } catch (error) {
                console.error('Error deleting video:', error);
                toast.error('Error deleting video - Please try again');
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    // Handle bulk delete
    const handleBulkDelete = async () => {
        if (selectedVideos.length === 0) {
            toast.warning('Please select videos to delete');
            return;
        }

        const content = `Deleting ${selectedVideos.length} videos. This action cannot be undone. Solve the math problem to confirm.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                setDeleteLoading(true);
                const response = await Calling.deleteVideo({ id: selectedVideos });

                if (response.status) {
                    toast.success(`Successfully deleted ${selectedVideos.length} videos`);
                    fetchVideos();
                } else {
                    toast.error(response.message || 'Failed to delete videos');
                }
            } catch (error) {
                console.error('Error deleting videos:', error);
                toast.error('Error deleting videos - Please try again');
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    // Handle set web status (individual or bulk)
    const handleSetWebStatus = async (ids, status) => {
        if (!ids || ids.length === 0) {
            toast.warning('Please select videos to update');
            return;
        }

        const statusText = status ? 'enable' : 'disable';
        const content = `${statusText.charAt(0).toUpperCase() + statusText.slice(1)} web access for ${ids.length} video(s)? Solve the math problem to confirm.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                setWebStatusLoading(true);
                const response = await Calling.setVideoStatus({ id: ids, status });

                if (response.status) {
                    toast.success(`Successfully ${statusText}d web access for ${ids.length} video(s)`);
                    fetchVideos();
                } else {
                    toast.error(response.message || `Failed to ${statusText} web access`);
                }
            } catch (error) {
                console.error(`Error setting web status:`, error);
                toast.error(`Error updating web status - Please try again`);
            } finally {
                setWebStatusLoading(false);
            }
        }
    };

    // Function to display pagination numbers with ellipsis for large page counts
    const getPaginationNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is less than max visible
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always show first page
            pageNumbers.push(1);

            // Calculate start and end of visible range
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            // Adjust if at the beginning or end
            if (currentPage <= 2) {
                endPage = Math.min(totalPages - 1, maxVisiblePages - 1);
            } else if (currentPage >= totalPages - 1) {
                startPage = Math.max(2, totalPages - maxVisiblePages + 2);
            }

            // Add ellipsis after first page if needed
            if (startPage > 2) {
                pageNumbers.push('...');
            }

            // Add middle pages
            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            // Add ellipsis before last page if needed
            if (endPage < totalPages - 1) {
                pageNumbers.push('...');
            }

            // Always show last page
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    return (
        <div className='w-full text-main-font mt-20 px-2 md:px-3 md:ml-[4.4rem]'>
            <div className="flex items-center justify-between">
                <h1 className="text-xl md:text-2xl font-bold text-main-font mt-4 w-auto">
                    Video Management
                </h1>
                <div className="text-sm text-gray-500">
                    Total: {totalRecords} videos
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <VideoNavbar selectState={'show'} />
                <hr className="border-gray-200" />

                {loader ? (
                    <div className="py-20">
                        <Loader loader={loader} />
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="bg-red-100 text-red-700 p-3 m-4 rounded-md">
                                {error}
                            </div>
                        )}

                        {/* Action Bar */}
                        <div className="p-4 flex flex-col md:flex-row flex-wrap justify-between items-start md:items-center gap-4">
                            <div className="flex flex-col w-full md:w-auto gap-4">
                                {/* Category Filter */}
                                <div className="flex flex-wrap gap-2">
                                    <span className="text-sm font-medium text-gray-700 self-center">Category:</span>
                                    <button
                                        onClick={() => handleCategoryChange(null)}
                                        className={`px-3 py-1 rounded-md transition-colors ${category === null ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                    >
                                        All
                                    </button>
                                    {categories.map(cat => (
                                        <button
                                            key={cat._id}
                                            onClick={() => handleCategoryChange(cat._id)}
                                            className={`px-3 py-1 rounded-md transition-colors ${category === cat._id ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                        >
                                            {cat.name}
                                        </button>
                                    ))}
                                </div>

                                {/* Web Status Filter */}
                                <div className="flex flex-wrap gap-2">
                                    <span className="text-sm font-medium text-gray-700 self-center">Web Status:</span>
                                    <button
                                        onClick={() => handleWebFilterChange('all')}
                                        className={`px-3 py-1 rounded-md transition-colors ${webFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => handleWebFilterChange('true')}
                                        className={`px-3 py-1 rounded-md transition-colors ${webFilter === 'true' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                    >
                                        Web Enabled
                                    </button>
                                    <button
                                        onClick={() => handleWebFilterChange('false')}
                                        className={`px-3 py-1 rounded-md transition-colors ${webFilter === 'false' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                    >
                                        Web Disabled
                                    </button>
                                </div>
                            </div>

                            {/* Bulk Actions */}
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm text-gray-500">
                                    {selectedVideos.length} selected
                                </span>
                                <button
                                    onClick={() => handleSetWebStatus(selectedVideos, true)}
                                    disabled={selectedVideos.length === 0 || webStatusLoading}
                                    className={`px-3 py-1 rounded-md ${selectedVideos.length === 0 || webStatusLoading
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-500 text-white hover:bg-green-600'
                                        }`}
                                >
                                    {webStatusLoading ? 'Updating...' : 'Enable Web'}
                                </button>
                                <button
                                    onClick={() => handleSetWebStatus(selectedVideos, false)}
                                    disabled={selectedVideos.length === 0 || webStatusLoading}
                                    className={`px-3 py-1 rounded-md ${selectedVideos.length === 0 || webStatusLoading
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                        }`}
                                >
                                    {webStatusLoading ? 'Updating...' : 'Disable Web'}
                                </button>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={selectedVideos.length === 0 || deleteLoading}
                                    className={`px-3 py-1 rounded-md ${selectedVideos.length === 0 || deleteLoading
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-500 text-white hover:bg-red-600'
                                        }`}
                                >
                                    {deleteLoading ? 'Deleting...' : 'Delete Selected'}
                                </button>
                            </div>
                        </div>

                        {/* Video Table/Grid */}
                        {videos && videos.length > 0 ? (
                            <div className="px-4 pb-4">
                                {/* Table Header */}
                                <div className="bg-gray-100 rounded-t-lg grid grid-cols-12 p-3 gap-2 font-medium text-gray-700">
                                    <div className="col-span-1 flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={() => setSelectAll(!selectAll)}
                                            className="h-4 w-4 text-blue-600 rounded"
                                        />
                                    </div>
                                    <div className="col-span-2">Thumbnail</div>
                                    <div className="col-span-3">Date</div>
                                    <div className="col-span-2">Web Status</div>
                                    <div className="col-span-4">Actions</div>
                                </div>

                                {/* Video Rows */}
                                <div className="rounded-b-lg border border-gray-200">
                                    {videos.map((video, index) => (
                                        <div
                                            key={video._id || index}
                                            className={`grid grid-cols-12 p-3 gap-2 items-center ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                } ${selectedVideos.includes(video._id)
                                                    ? 'bg-blue-50 border-l-4 border-blue-500'
                                                    : ''
                                                }`}
                                        >
                                            <div className="col-span-1 flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedVideos.includes(video._id)}
                                                    onChange={() => handleVideoSelect(video._id)}
                                                    className="h-4 w-4 text-blue-600 rounded"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <div className="h-16 w-28 relative rounded overflow-hidden">
                                                    <img
                                                        src={video.thumbnail}
                                                        alt="Video thumbnail"
                                                        className="absolute h-full w-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = '/images/placeholder-image.jpg';
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-span-3 text-gray-600">
                                                {new Date(video.createdAt).toLocaleDateString()}
                                            </div>
                                            <div className="col-span-2">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${video.web ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {video.web ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </div>
                                            <div className="col-span-4 flex flex-wrap gap-2">
                                                <a
                                                    href={`${video.video}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md text-sm font-medium"
                                                >
                                                    View
                                                </a>
                                                <button
                                                    onClick={() => handleSetWebStatus([video._id], !video.web)}
                                                    className={`px-3 py-1 rounded-md text-sm font-medium ${video.web
                                                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        }`}
                                                >
                                                    {video.web ? 'Disable Web' : 'Enable Web'}
                                                </button>
                                                <button
                                                    className="px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded-md text-sm font-medium"
                                                    onClick={() => handleSingleDelete(video._id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No videos found with the selected filters.</p>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center mt-2 mb-4">
                                <div className="flex flex-wrap items-center gap-1">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`}
                                    >
                                        Previous
                                    </button>

                                    {/* Page numbers */}
                                    {getPaginationNumbers().map((page, index) => (
                                        page === '...' ? (
                                            <span key={`ellipsis-${index}`} className="px-3 py-1">...</span>
                                        ) : (
                                            <button
                                                key={`page-${page}`}
                                                onClick={() => handlePageChange(page)}
                                                className={`px-3 py-1 rounded-md ${currentPage === page ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                            >
                                                {page}
                                            </button>
                                        )
                                    ))}
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`px-3 py-1 rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Records per page info */}
                        {videos && videos.length > 0 && (
                            <div className="text-center pb-4 text-xs text-gray-500">
                                Showing {videos.length} of {totalRecords} videos
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default VideoShow;